import { createSelector } from 'reselect'
import config from 'constants/config'
import { getCanViewLuxPlusBenefits, getHasLuxPlusInAnyRegion, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getCurrentUserId } from 'selectors/accountSelectors'
import { runChannelMarkupV2SessionRound } from 'lib/channelMarkup/channelMarkupV2'
import type { ChannelMarkupSource, ChannelMarkupV2SessionContext } from 'lib/channelMarkup/channelMarkupV2'
import type { ChannelMarkupLeadOffer } from 'lib/channelMarkup/channelMarkupLeadPricing'

export const getChannelMarkup = (state: App.State) => config.ENABLE_CHANNEL_MARKUP ? state.marketing.channelMarkup : undefined

/**
 * The account says it has a tier but the subscription has not been answered yet: it is being
 * fetched, or `fetchMemberSubscription` will still fetch it. Every answer bumps
 * `subscriptionRevision`, so a fetch that failed, came back empty, or will never run (LuxPlus+
 * disabled in this region) is settled rather than pending.
 */
function getIsSubscriptionPending(state: App.State): boolean {
  const member = state.auth.account.luxPlus.member
  const { subscription } = member
  if (!state.auth.account.subscriberTier || subscription.item || !getIsLuxPlusEnabled(state)) {
    return false
  }
  return subscription.fetching || (!subscription.error && !member.subscriptionRevision)
}

/**
 * Subscriber tier only carries meaning for a member who can currently view LuxPlus+ benefits, or a
 * paid member of any region (membership region does not matter to a campaign's tier conditions);
 * everyone else evaluates as `null` (a known non-member). While the subscription is pending the
 * tier is unknown: return `undefined` so tier conditions stay pending instead of briefly pricing
 * a member as a non-member. Once the subscription is settled without an item, the user is a
 * known non-member.
 */
function getChannelMarkupV2SubscriberTier(state: App.State): string | null | undefined {
  if (getIsSubscriptionPending(state)) {
    return undefined
  }
  const isMember = getCanViewLuxPlusBenefits(state) || getHasLuxPlusInAnyRegion(state)
  return isMember ? (state.auth.account.luxPlus.member.subscription.item?.tier ?? null) : null
}

/**
 * The visitor as the live account sees them now. The server prices each render as this user
 * ({@link App.ChannelMarkupV2SessionUser}). Undefined while the subscriber tier is still pending,
 * so a member is never priced as a non-member.
 */
export function getLiveChannelMarkupV2SessionUser(state: App.State): App.ChannelMarkupV2SessionUser | undefined {
  const subscriberTier = getChannelMarkupV2SubscriberTier(state)
  if (subscriberTier === undefined) {
    return undefined
  }
  return {
    region: state.geo.currentRegionCode,
    userId: getCurrentUserId(state) || null,
    subscriberTier,
    roles: state.auth.account.roles,
  }
}

/**
 * Whether the live user would price differently from the user the page was priced as: a different
 * subscriber tier or different roles. The user id alone never changes the price.
 */
export function isChannelMarkupV2PricingChange(
  sessionUser: App.ChannelMarkupV2SessionUser,
  liveUser: App.ChannelMarkupV2SessionUser,
): boolean {
  return liveUser.subscriberTier !== sessionUser.subscriberTier ||
    liveUser.roles.length !== sessionUser.roles.length ||
    liveUser.roles.some(role => !sessionUser.roles.includes(role))
}

const getSessionUser = (state: App.State) => state.marketing.channelMarkupV2SessionUser

/** Identifies the prices a user gets, so the reload guard can tell one pricing change from another. */
function getChannelMarkupV2PricingKey(user: App.ChannelMarkupV2SessionUser): string {
  return JSON.stringify([user.subscriberTier, [...user.roles].sort()])
}

/**
 * The pricing to reload the page for: set when the live user (after signing in, logging out, or
 * joining LuxPlus+) would price differently from the user this page was priced as, so the next
 * server render prices them as they are now. Undefined when no reload is needed: no v2 markup
 * applies (the price cannot change, and the next server render reads the live account anyway),
 * the page was not priced as a known user, or the live tier is still pending.
 */
export function getChannelMarkupV2RefreshKey(state: App.State): string | undefined {
  if (!config.MARKETING_ROUTER_ENABLED || !state.marketing.channelMarkupV2) {
    return undefined
  }
  const sessionUser = getSessionUser(state)
  const liveUser = getLiveChannelMarkupV2SessionUser(state)
  if (!sessionUser || !liveUser || !isChannelMarkupV2PricingChange(sessionUser, liveUser)) {
    return undefined
  }
  return getChannelMarkupV2PricingKey(liveUser)
}

/**
 * Whether the page is priced as the live user is now. False while the live tier is pending, so a
 * reload guard is only cleared once the reload it guarded has really repriced the page.
 */
export function getIsChannelMarkupV2PricedAsLiveUser(state: App.State): boolean {
  const sessionUser = getSessionUser(state)
  const liveUser = getLiveChannelMarkupV2SessionUser(state)
  return !!sessionUser && !!liveUser && !isChannelMarkupV2PricingChange(sessionUser, liveUser)
}

/**
 * The channel markup v2 session (user + market) context used by `getChannelMarkupSource`
 * to run the session round. The user half is the user the server priced this page as, so prices
 * stay consistent until a pricing change reloads the page (see `useChannelMarkupV2PriceRefresh`);
 * when the server priced no one (e.g. the subscriber tier was still pending) the live account is used. Memoized on its primitive inputs so it
 * can be composed as an input selector without breaking `getChannelMarkupSource`'s memoization.
 */
export const getChannelMarkupV2SessionContext = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => {
    const sessionUser = getSessionUser(state)
    return sessionUser ? sessionUser.userId : getCurrentUserId(state) || null
  },
  (state: App.State) => {
    const sessionUser = getSessionUser(state)
    return sessionUser ? sessionUser.subscriberTier : getChannelMarkupV2SubscriberTier(state)
  },
  (state: App.State) => getSessionUser(state)?.roles ?? state.auth.account.roles,
  (region, currency, userId, subscriberTier, roles): ChannelMarkupV2SessionContext => ({ region, currency, userId, subscriberTier, roles }),
)

/**
 * The mode-tagged markup source to thread through the offer mappers. Returns the v2 session
 * state when channel markup v2 is active and the marketing router selected a markup; otherwise
 * falls back to the v1 `channelMarkup`.
 *
 * The v2 markup (`state.marketing.channelMarkupV2`) is decided ONCE at SSR — the marketing
 * router uses weighted-random variant assignment, so evaluating it here on the client would
 * re-roll the variant and drift the price between the SSR render and hydration. This selector
 * only reads that persisted decision and runs the deterministic session round against it.
 *
 * Memoized: the v2 session round rebuilds a fresh object on every call, so reactive
 * `useAppSelector` consumers need a stable reference to avoid re-rendering on every store
 * update. It recomputes only when its inputs (resolved markup, session context, v1 markup)
 * actually change.
 */
export const getChannelMarkupSource = createSelector(
  (state: App.State) => state.marketing.channelMarkupV2,
  (state: App.State) => getChannelMarkupV2SessionContext(state),
  (state: App.State) => getChannelMarkup(state),
  (channelMarkupV2, sessionContext, channelMarkup): ChannelMarkupSource => {
    // The Marketing Router flag alone selects v2: it is the same flag that evaluated the request
    // and tagged the landing, so the rendered price always matches the advertised variant.
    const isV2Active = config.MARKETING_ROUTER_ENABLED
      && !!channelMarkupV2
      && !!sessionContext.region
      && !!sessionContext.currency

    if (isV2Active) {
      // The router has no global offer-id block list, so the v2 source's block list is empty.
      const cfg = { channelMarkupListV1: [], channelMarkupListV2: [channelMarkupV2], globalOfferIdBlockList: [] }
      const { sessionState, channelMarkupId } = runChannelMarkupV2SessionRound(cfg, sessionContext)
      return { mode: 'v2', sessionState, blockList: [], channelMarkupId }
    }

    return { mode: 'v1', channelMarkup }
  },
)

/**
 * The loaded offer a v2 price band reads its lead price from: a Flash/LPC offer, else the bedbank
 * offer or, before it loads, the bedbank summary a search tile renders from.
 */
export function getChannelMarkupLeadOffer(state: App.State, offerId: string): ChannelMarkupLeadOffer | undefined {
  return state.offer.offers[offerId] ?? state.offer.bedbankOffers[offerId] ?? state.offer.bedbankOfferSummaries[offerId]
}
